import { atom, useSetAtom, useAtomValue } from 'jotai'
import { useCallback, useMemo } from 'react'
import { useSetPendingMessage, useSetStreamingMetadata } from './messages'
import { useModals } from './modals'
import { useSetMessageMentions } from '../components/chat/input/mentions/atoms'
import { atomWithGlobalStore, atomWithGlobalState } from '@highlight/ipc/jotai'
import { useResetChatContext } from './atoms'
import { useIsTurnLive } from './turn-status'
import { stableConversationKey, type ChatIdMapping } from '../utils/stable-conversation-key'

/**
 * Active chat atom management
 */
const activeChatAtom = atom<string | undefined>(undefined)

// Temp ID → Real ID mapping for recovery when sidepanel closes during ID transition (HIG-4293)
// Bounded to 20 entries to prevent unbounded growth
const MAX_CHAT_ID_MAPPINGS = 20
const chatIdMappingAtom = atomWithGlobalStore<ChatIdMapping>('chat', 'chat-id-mapping', {})
const isDummyChatAtom = atomWithGlobalState<boolean>('isDummyChat', false)
const collapseCodeBlocksAtom = atomWithGlobalStore<boolean>('settings', 'collapse-code-blocks', false)
const autoMemoryCollectionAtom = atomWithGlobalStore<boolean>('settings', 'auto-memory-collection', true)

export type ChatOnboardingStep = 'name' | 'work-info' | 'use-case' | 'role' | 'completed'

export const chatOnboardingStepAtom = atom<ChatOnboardingStep>('role')

/**
 * Get and set hooks for active chat ID.
 */
export const useActiveChatId = () => {
  return useAtomValue(activeChatAtom)
}
export const useSetActiveChat = () => {
  return useSetAtom(activeChatAtom)
}

/**
 * Whether the ACTIVE chat's turn is live (submitting or streaming). This is
 * the per-chat signal — never a global boolean that stays true while ANY chat
 * streams (HIG-5962) — for behavior keyed to "this chat's turn": loaders,
 * previews, follow mode, scroll behavior, input gating.
 *
 * Explicit-chat variants live in `turn-status.ts`: `useIsTurnLive(chatId)`
 * (this hook is just that, bound to the active chat) and the full
 * `useTurnStatus(chatId)`. Metadata analog, explicit by contrast because its
 * callers already hold a chat id: `useStreamingMetadataForChat`.
 */
export const useIsActiveTurn = (): boolean => {
  const activeChatId = useActiveChatId()
  return useIsTurnLive(activeChatId)
}

/**
 * Get and set hooks for collapse code blocks.
 */
export const useCollapseCodeBlocks = () => {
  return useAtomValue(collapseCodeBlocksAtom)
}

/**
 * Get and set hooks for auto memory collection.
 */
export const useAutoMemoryCollection = () => {
  return useAtomValue(autoMemoryCollectionAtom)
}

/**
 * During on boarding the user is prompted to start their first chat. We mock a fake assistant message
 * and hide several buttons to make the experience more approachable. If you change when/how this is set,
 * make sure to go through the onboarding flow and test it.
 */
export const useIsDummyChat = () => {
  return useAtomValue(isDummyChatAtom)
}

export const useSetIsDummyChat = () => {
  return useSetAtom(isDummyChatAtom)
}

export const useChatOnboardingStep = () => {
  return useAtomValue(chatOnboardingStepAtom)
}

export const useSetChatOnboardingStep = () => {
  return useSetAtom(chatOnboardingStepAtom)
}

/**
 * Chat ID mapping hooks for temp ID → real ID recovery (HIG-4293)
 */
export const useAddChatIdMapping = () => {
  const setMapping = useSetAtom(chatIdMappingAtom)
  return useCallback(
    (tempId: string, realId: string) => {
      setMapping((prev) => {
        const entries = Object.entries(prev)
        // Keep only the most recent MAX entries
        const trimmed =
          entries.length >= MAX_CHAT_ID_MAPPINGS ? Object.fromEntries(entries.slice(-MAX_CHAT_ID_MAPPINGS + 1)) : prev
        return { ...trimmed, [tempId]: realId }
      })
    },
    [setMapping],
  )
}

export const useRemoveChatIdMapping = () => {
  const setMapping = useSetAtom(chatIdMappingAtom)
  return useCallback(
    (tempId: string) => {
      setMapping((prev) => {
        const { [tempId]: _, ...rest } = prev
        return rest
      })
    },
    [setMapping],
  )
}

export const useGetRealChatId = () => {
  const mapping = useAtomValue(chatIdMappingAtom)
  return useCallback(
    (chatId: string): string => {
      return mapping[chatId] ?? chatId
    },
    [mapping],
  )
}

/**
 * Swap-stable render identity for a chat. The mapping write
 * (`addChatIdMapping`) lands before `setActiveChatId` in the same metadata
 * handler, so the reverse entry always exists by the time the active id
 * changes — the returned key literally never changes value across the swap.
 */
export const useStableConversationKey = (chatId: string | undefined): string | undefined => {
  const mapping = useAtomValue(chatIdMappingAtom)
  return useMemo(() => (chatId ? stableConversationKey(mapping, chatId) : undefined), [mapping, chatId])
}

export const useCloseActiveChat = () => {
  const setActiveChatId = useSetActiveChat()
  const setStreamingMetadata = useSetStreamingMetadata()
  const setPendingMessage = useSetPendingMessage()
  const setMessageMentions = useSetMessageMentions()
  const { clearModals } = useModals()
  const setIsDummyChat = useSetIsDummyChat()
  const resetContext = useResetChatContext()

  // Input is preserved here so navigating between chats/home doesn't drop the
  // user's in-progress composition. Callers that need a fresh input (logout,
  // tray "new chat") call setChatInput('') explicitly.
  return useCallback((options?: { keepAttachments?: boolean }) => {
    setActiveChatId(undefined)
    setMessageMentions([])
    if (!options?.keepAttachments) {
      resetContext({ keepViewScoped: true })
    }
    setStreamingMetadata(undefined)
    setPendingMessage(undefined)
    clearModals()
    setIsDummyChat(false)
  }, [])
}
